import React from 'react'
import { ScaleSymLog, ScaleLinear } from 'd3-scale'
import { min, max, ticks } from 'd3-array'
import {
  TICKS_COUNTER,
  SPACE_BETWEEN_AXIS_AND_SCATTER_AREA,
  DRAGGABLE_BAR_WIDTH,
  TICK_LENGHT,
  LABEL_TICKS_COUNTER,
  SPACE_BETWEEN_AXIS_AND_AXIS_LABEL,
} from '../constants'
import { isBetween } from '../../../lib/math-utils'
import { NumericRange } from '../types'
import { formatSIWithIntegerThreshold } from '../../../lib/format-utils'

const GRID_COLOR = 'var(--black)'
const GRID_OPACITY = 0.08
const ZERO_GRID_OPACITY = 0.3
const TICK_COLOR = 'var(--black)'
const TICK_OPACITY = 0.9
const LABEL_COLOR = 'var(--gray300)'
const SPAN_HIDE_LABEL_VERT = 20
const SPAN_HIDE_LABEL_HORIZ = 35

interface Props {
  className?: string
  xScale: ScaleSymLog<number, number> | ScaleLinear<number, number>
  yScale: ScaleSymLog<number, number> | ScaleLinear<number, number>
  xScaleScrollbar: ScaleSymLog<number, number> | ScaleLinear<number, number>
  yScaleScrollbar: ScaleSymLog<number, number> | ScaleLinear<number, number>
}

export const Grid: React.FC<Props> = ({
  className = '',
  xScale,
  yScale,
  xScaleScrollbar,
  yScaleScrollbar,
}) => {
  const xTicks = xScaleScrollbar.ticks(TICKS_COUNTER) as number[]
  const yTicks = yScaleScrollbar.ticks(TICKS_COUNTER) as number[]

  const visibleXTicks = xTicks.filter((t) => isBetween(xScale.range() as NumericRange, xScale(t)))
  const visibleYTicks = yTicks.filter((t) => isBetween(yScale.range() as NumericRange, yScale(t)))

  const xTicksValues = ticks(min(xTicks), max(xTicks), LABEL_TICKS_COUNTER)
  const yTicksValues = ticks(min(yTicks), max(yTicks), LABEL_TICKS_COUNTER)

  const xShowTickLabelAreaRange = [
    xScale.range()[0] + SPAN_HIDE_LABEL_HORIZ,
    xScale.range()[1] - SPAN_HIDE_LABEL_HORIZ,
  ] as NumericRange
  const yShowTickLabelAreaRange = [
    yScale.range()[1] + SPAN_HIDE_LABEL_VERT,
    yScale.range()[0] - SPAN_HIDE_LABEL_VERT,
  ] as NumericRange

  return (
    <g className={`-grid ${className}`}>
      <g className="-vertical-ticks">
        {visibleXTicks.map((tick) => {
          const x = xScale(tick)
          const yBottom = yScale.range()[0]
          const yTop = yScale.range()[1]
          const showLabel = xTicksValues.includes(tick) && isBetween(xShowTickLabelAreaRange, x)
          const yTickEnd = yBottom + SPACE_BETWEEN_AXIS_AND_SCATTER_AREA

          return (
            <React.Fragment key={tick}>
              <line
                x1={x}
                y1={yBottom}
                x2={x}
                y2={yTop}
                stroke={GRID_COLOR}
                strokeOpacity={tick === 0 ? ZERO_GRID_OPACITY : GRID_OPACITY}
              />
              <line
                x1={x}
                y1={yTickEnd}
                x2={x}
                y2={yTickEnd + TICK_LENGHT}
                stroke={TICK_COLOR}
                strokeOpacity={TICK_OPACITY}
              />
              {showLabel && (
                <text
                  key={tick}
                  className="fw5"
                  x={xScale(tick)}
                  y={yTickEnd + SPACE_BETWEEN_AXIS_AND_AXIS_LABEL}
                  fill={LABEL_COLOR}
                  fontSize={10}
                  textAnchor="middle"
                  dominantBaseline="hanging"
                >
                  {formatSIWithIntegerThreshold(tick)}
                </text>
              )}
            </React.Fragment>
          )
        })}
      </g>

      <g className="-horiz-ticks">
        {visibleYTicks.map((tick) => {
          const y = yScale(tick)
          const xLeft = xScale.range()[0]
          const xRight = xScale.range()[1]
          const showLabel = yTicksValues.includes(tick) && isBetween(yShowTickLabelAreaRange, y)
          const xTickEnd = xLeft - SPACE_BETWEEN_AXIS_AND_SCATTER_AREA

          return (
            <React.Fragment key={tick}>
              <line
                x1={xLeft}
                y1={yScale(tick)}
                x2={xRight}
                y2={y}
                stroke={GRID_COLOR}
                strokeOpacity={tick === 0 ? ZERO_GRID_OPACITY : GRID_OPACITY}
              />
              <line
                x1={xTickEnd - TICK_LENGHT - DRAGGABLE_BAR_WIDTH}
                y1={y}
                x2={xTickEnd - DRAGGABLE_BAR_WIDTH}
                y2={y}
                stroke={TICK_COLOR}
                strokeOpacity={TICK_OPACITY}
              />
              {showLabel && (
                <text
                  key={tick}
                  className="fw5"
                  x={xTickEnd - SPACE_BETWEEN_AXIS_AND_AXIS_LABEL}
                  y={yScale(tick)}
                  fill={LABEL_COLOR}
                  fontSize={10}
                  textAnchor="end"
                  dominantBaseline="middle"
                >
                  {formatSIWithIntegerThreshold(tick)}
                </text>
              )}
            </React.Fragment>
          )
        })}
      </g>
    </g>
  )
}
